export interface BusinessMetric {
  value: number
  suffix: string
  prefix?: string
  label: string
  description: string
}

export interface CustomerStory {
  name: string
  industry: string
  headline: string
  metrics: string[]
  quote?: string
  caseStudyUrl?: string
}

export interface ValueProposition {
  icon: string
  title: string
  description: string
}

export interface BusinessOutcome {
  title: string
  description: string
  metrics: BusinessMetric[]
}

export interface IcpData {
  id: string
  title: string
  shortDescription: string
  fullDescription: string
  icon: string
  color: string
  gradient: { from: string; to: string }
  keyMetrics: BusinessMetric[]
  valuePropositions: ValueProposition[]
  businessOutcomes: BusinessOutcome[]
  customerStories: CustomerStory[]
  challenges: string[]
  hivemqSolution: string[]
  ctaTitle: string
  ctaDescription: string
  ctaRoute: string
}

export const smartManufacturing: IcpData = {
  id: 'smart-manufacturing',
  title: 'Smart Manufacturing',
  shortDescription:
    'Transform factory operations with real-time data connectivity, unified namespaces, and predictive analytics.',
  fullDescription:
    "HiveMQ powers the world's most advanced smart factories by enabling seamless connectivity between OT and IT systems. From shop-floor sensors to enterprise analytics, our MQTT platform delivers the real-time data backbone that drives Industry 4.0 transformation.",
  icon: 'Factory',
  color: 'blue.500',
  gradient: { from: 'blue.600', to: 'blue.400' },
  keyMetrics: [
    {
      value: 40,
      suffix: '%',
      label: 'Downtime Reduction',
      description: 'Average reduction in unplanned downtime across manufacturing deployments',
    },
    {
      value: 15,
      suffix: '%',
      label: 'OEE Increase',
      description: 'Improvement in Overall Equipment Effectiveness through real-time monitoring',
    },
    {
      value: 400,
      suffix: 'M',
      prefix: '$',
      label: 'Cost Savings',
      description: 'Documented savings achieved by Eli Lilly through HiveMQ-powered automation',
    },
    {
      value: 470,
      suffix: 'M+',
      label: 'Messages/Month',
      description: "Monthly message throughput for Daimler's connected factory platform",
    },
  ],
  valuePropositions: [
    {
      icon: 'Network',
      title: 'Unified Namespace',
      description:
        'Create a single source of truth for all factory data with a standardized MQTT topic hierarchy spanning OT and IT.',
    },
    {
      icon: 'Eye',
      title: 'Real-Time Visibility',
      description:
        'Monitor every machine, sensor, and production line in real time with sub-millisecond message delivery.',
    },
    {
      icon: 'ShieldCheck',
      title: 'Zero Message Loss',
      description:
        'Guarantee delivery of every critical event with QoS levels, persistent sessions, and disk-based persistence.',
    },
    {
      icon: 'Cpu',
      title: 'Edge Intelligence',
      description:
        'Process and filter data at the edge to reduce bandwidth costs and enable local decision-making.',
    },
    {
      icon: 'ArrowLeftRight',
      title: 'Protocol Bridge',
      description:
        'Connect legacy OPC-UA, Modbus, and proprietary protocols to your MQTT infrastructure without rip-and-replace.',
    },
    {
      icon: 'Layers',
      title: 'Scalable Architecture',
      description:
        'Scale from a single production line to hundreds of factories with clustering and elastic throughput.',
    },
  ],
  businessOutcomes: [
    {
      title: 'Predictive Maintenance',
      description:
        'Shift from reactive to predictive maintenance using real-time equipment telemetry.',
      metrics: [
        {
          value: 40,
          suffix: '%',
          label: 'Less Downtime',
          description: 'Reduction in unplanned equipment failures',
        },
        {
          value: 25,
          suffix: '%',
          label: 'Maintenance Savings',
          description: 'Lower maintenance costs through predictive scheduling',
        },
      ],
    },
    {
      title: 'Production Optimization',
      description:
        'Optimize throughput and quality with closed-loop feedback from every production stage.',
      metrics: [
        {
          value: 15,
          suffix: '%',
          label: 'OEE Improvement',
          description: 'Higher overall equipment effectiveness',
        },
        {
          value: 20,
          suffix: '%',
          label: 'Scrap Reduction',
          description: 'Less waste through real-time quality control',
        },
      ],
    },
    {
      title: 'Supply Chain Integration',
      description:
        'Connect shop floor to top floor with real-time production data flowing to ERP and MES.',
      metrics: [
        {
          value: 30,
          suffix: '%',
          label: 'Faster Response',
          description: 'Quicker reaction to supply chain disruptions',
        },
        {
          value: 10,
          suffix: 'x',
          label: 'Data Freshness',
          description: 'Improvement in data update frequency vs batch systems',
        },
      ],
    },
  ],
  customerStories: [
    {
      name: 'Daimler',
      industry: 'Automotive Manufacturing',
      headline: 'Connecting 24 factories and 10,000+ devices on a unified MQTT platform',
      metrics: [
        '24 factories connected globally',
        '10,000+ industrial devices integrated',
        '470M+ messages processed monthly',
        'Sub-second data delivery across sites',
      ],
      quote:
        'HiveMQ enabled us to build a truly unified data backbone across our global manufacturing operations.',
    },
    {
      name: 'Eli Lilly',
      industry: 'Pharmaceutical Manufacturing',
      headline: 'Achieving $400M in savings through connected manufacturing',
      metrics: [
        '15 factories connected',
        '$400M documented cost savings',
        'Real-time batch monitoring',
        'FDA compliance maintained',
      ],
    },
    {
      name: 'BMW',
      industry: 'Automotive Manufacturing',
      headline: 'Real-time production monitoring across assembly lines',
      metrics: [
        'Multiple plants connected',
        'Real-time quality monitoring',
        'Automated defect detection',
        'Reduced line stoppages',
      ],
    },
    {
      name: 'Audi',
      industry: 'Automotive Manufacturing',
      headline: 'Industry 4.0 transformation with unified data architecture',
      metrics: [
        'Smart factory initiative',
        'Connected robotics and sensors',
        'Real-time production analytics',
        'Seamless IT/OT convergence',
      ],
    },
    {
      name: 'ZF Friedrichshafen',
      industry: 'Automotive Supplier',
      headline: 'Global sensor connectivity for predictive quality assurance',
      metrics: [
        'Multi-site deployment',
        'Predictive quality analytics',
        'Sensor data integration',
        'Reduced warranty claims',
      ],
    },
  ],
  challenges: [
    'Fragmented data silos across factory floor systems',
    'Legacy protocols that cannot communicate with modern cloud platforms',
    'Unplanned downtime costing millions per hour',
    'Lack of real-time visibility into production performance',
    'Difficulty scaling connectivity across multiple global sites',
    'Ensuring zero data loss for critical safety and quality events',
  ],
  hivemqSolution: [
    'Unified Namespace architecture connecting all OT and IT data sources',
    'Protocol adapters for OPC-UA, Modbus, Siemens S7, and more',
    'Predictive maintenance through real-time equipment telemetry streaming',
    'Live dashboards with sub-second updates from every production line',
    'Elastic clustering that scales from one factory to hundreds globally',
    'QoS guarantees and disk persistence ensuring zero message loss',
  ],
  ctaTitle: 'See Smart Manufacturing in Action',
  ctaDescription:
    'Explore our interactive factory demo showcasing real-time sensor data, UNS architecture, and predictive analytics.',
  ctaRoute: '/factory',
}

export const energy: IcpData = {
  id: 'energy',
  title: 'Energy & Utilities',
  shortDescription:
    'Monitor renewable assets, optimize grid operations, and enable predictive maintenance across distributed infrastructure.',
  fullDescription:
    'HiveMQ enables energy companies to connect and monitor distributed generation assets, optimize grid operations, and transition to smarter, more sustainable energy systems. Our platform handles the unique challenges of remote, intermittent, and highly distributed energy infrastructure.',
  icon: 'Zap',
  color: 'green.500',
  gradient: { from: 'green.600', to: 'green.400' },
  keyMetrics: [
    {
      value: 100,
      suffix: 'M+',
      prefix: '$',
      label: 'Cost Savings',
      description:
        'Savings achieved by NextEra Energy through predictive maintenance and optimization',
    },
    {
      value: 25,
      suffix: '%',
      label: 'Reduced Downtime',
      description:
        'Decrease in asset downtime through real-time monitoring and predictive analytics',
    },
    {
      value: 3000,
      suffix: '+',
      label: 'Connected Customers',
      description: "End customers connected through Fortum Spring's HiveMQ-powered platform",
    },
    {
      value: 12,
      suffix: '%',
      label: 'Yield Increase',
      description: 'Improvement in energy yield through optimized asset performance',
    },
  ],
  valuePropositions: [
    {
      icon: 'Sun',
      title: 'Renewable Monitoring',
      description:
        'Monitor solar panels, wind turbines, and battery storage systems in real time across geographically dispersed sites.',
    },
    {
      icon: 'Wrench',
      title: 'Predictive Maintenance',
      description:
        'Detect anomalies early and schedule maintenance proactively to avoid costly unplanned outages.',
    },
    {
      icon: 'MapPin',
      title: 'Distributed Assets',
      description:
        'Connect thousands of remote assets across wide geographies with reliable, low-bandwidth MQTT messaging.',
    },
    {
      icon: 'BarChart3',
      title: 'Grid Optimization',
      description:
        'Balance load, manage demand response, and optimize grid stability with real-time operational data.',
    },
    {
      icon: 'WifiOff',
      title: 'Offline Resilience',
      description:
        'Queue and deliver messages reliably even when remote sites experience intermittent connectivity.',
    },
    {
      icon: 'Monitor',
      title: 'SCADA Integration',
      description:
        'Bridge existing SCADA systems to modern cloud analytics without disrupting critical operations.',
    },
  ],
  businessOutcomes: [
    {
      title: 'Asset Performance Management',
      description: 'Maximize energy output and equipment lifespan through continuous monitoring.',
      metrics: [
        {
          value: 12,
          suffix: '%',
          label: 'Yield Increase',
          description: 'Higher energy output from optimized assets',
        },
        {
          value: 30,
          suffix: '%',
          label: 'Longer Asset Life',
          description: 'Extended equipment lifespan through proactive care',
        },
      ],
    },
    {
      title: 'Operational Efficiency',
      description: 'Reduce operational costs through automation and intelligent decision-making.',
      metrics: [
        {
          value: 25,
          suffix: '%',
          label: 'Less Downtime',
          description: 'Reduction in unplanned asset outages',
        },
        {
          value: 20,
          suffix: '%',
          label: 'OpEx Savings',
          description: 'Lower operational expenditure through automation',
        },
      ],
    },
    {
      title: 'Grid Modernization',
      description:
        'Enable smarter grid operations with real-time data from distributed energy resources.',
      metrics: [
        {
          value: 50,
          suffix: '%',
          label: 'Faster Response',
          description: 'Quicker grid balancing and demand response',
        },
        {
          value: 15,
          suffix: '%',
          label: 'Loss Reduction',
          description: 'Decrease in transmission and distribution losses',
        },
      ],
    },
  ],
  customerStories: [
    {
      name: 'NextEra Energy',
      industry: 'Renewable Energy',
      headline: 'Achieving $100M+ savings through predictive wind and solar asset management',
      metrics: [
        '$100M+ in documented savings',
        'Thousands of wind turbines monitored',
        'Predictive maintenance across fleet',
        'Real-time performance optimization',
      ],
    },
    {
      name: 'Fortum Spring',
      industry: 'Energy Services',
      headline: 'Connecting 3,000+ customers on a smart energy platform',
      metrics: [
        '3,000+ connected customers',
        'Real-time energy consumption data',
        'Automated demand response',
        'Customer engagement platform',
      ],
      quote:
        'HiveMQ provides the reliable messaging backbone that our smart energy platform depends on.',
    },
    {
      name: 'Statkraft',
      industry: 'Hydroelectric Power',
      headline: 'Modernizing hydroelectric operations with real-time telemetry',
      metrics: [
        'Multiple hydroelectric plants',
        'Real-time water flow monitoring',
        'Automated turbine optimization',
        'Remote operations capability',
      ],
    },
    {
      name: 'Stellar Renewable Power',
      industry: 'Solar Energy',
      headline: 'Scaling solar farm monitoring across distributed installations',
      metrics: [
        'Distributed solar installations',
        'Panel-level performance monitoring',
        'Inverter health tracking',
        'Weather-adaptive optimization',
      ],
    },
  ],
  challenges: [
    'Remote assets with unreliable network connectivity',
    'Legacy SCADA systems unable to integrate with cloud platforms',
    'Massive scale of distributed generation assets to monitor',
    'Real-time grid balancing requiring sub-second data delivery',
    'Regulatory compliance for critical infrastructure data',
    'High cost of unplanned maintenance for remote equipment',
  ],
  hivemqSolution: [
    'Offline-capable MQTT messaging with store-and-forward for remote sites',
    'SCADA protocol bridges connecting legacy systems to modern analytics',
    'Elastic scaling supporting millions of connected sensors and meters',
    'Sub-second message delivery for real-time grid operations',
    'Enterprise-grade security with TLS, RBAC, and audit logging',
    'Predictive maintenance through continuous equipment health streaming',
  ],
  ctaTitle: 'See Energy Solutions in Action',
  ctaDescription:
    'Explore our interactive energy demo showcasing renewable asset monitoring, grid analytics, and predictive maintenance.',
  ctaRoute: '/energy',
}

export const dataCenters: IcpData = {
  id: 'data-centers',
  title: 'Data Centers',
  shortDescription:
    'Optimize cooling efficiency, ensure uptime, and gain multi-site visibility across data center infrastructure.',
  fullDescription:
    'HiveMQ enables data center operators to connect thousands of environmental sensors, power monitoring systems, and cooling infrastructure into a unified real-time platform. Our MQTT broker delivers the reliability and scale needed for mission-critical facility management.',
  icon: 'Server',
  color: 'purple.500',
  gradient: { from: 'purple.600', to: 'purple.400' },
  keyMetrics: [
    {
      value: 30,
      suffix: '%',
      label: 'Cooling Cost Reduction',
      description: 'Savings in cooling energy costs through real-time environmental optimization',
    },
    {
      value: 99.999,
      suffix: '%',
      label: 'Uptime Achieved',
      description: 'Five-nines availability maintained with proactive monitoring and alerting',
    },
    {
      value: 50,
      suffix: 'K+',
      label: 'Sensors per Facility',
      description: 'Environmental and power sensors connected per data center facility',
    },
    {
      value: 15,
      suffix: 'min',
      label: 'Faster Incident Response',
      description: 'Reduction in mean time to detect and respond to facility incidents',
    },
  ],
  valuePropositions: [
    {
      icon: 'Thermometer',
      title: 'Environmental Monitoring',
      description:
        'Track temperature, humidity, and airflow across every rack and aisle with real-time sensor data.',
    },
    {
      icon: 'BarChart3',
      title: 'Capacity Planning',
      description:
        'Make data-driven decisions about power and cooling capacity with historical trends and predictive models.',
    },
    {
      icon: 'Shield',
      title: 'Uptime Assurance',
      description:
        'Detect anomalies before they cause outages with intelligent alerting and automated remediation workflows.',
    },
    {
      icon: 'Zap',
      title: 'Power Optimization',
      description:
        'Monitor PUE, optimize power distribution, and reduce energy waste across the facility.',
    },
    {
      icon: 'Globe',
      title: 'Multi-Site Visibility',
      description:
        'Manage multiple data center facilities from a single pane of glass with unified data streaming.',
    },
    {
      icon: 'Lock',
      title: 'Security & Compliance',
      description:
        'Meet SOC 2, ISO 27001, and industry compliance requirements with encrypted, auditable data streams.',
    },
  ],
  businessOutcomes: [
    {
      title: 'Energy Efficiency',
      description: 'Dramatically reduce cooling and power costs through intelligent optimization.',
      metrics: [
        {
          value: 30,
          suffix: '%',
          label: 'Cooling Savings',
          description: 'Reduction in cooling-related energy costs',
        },
        {
          value: 20,
          suffix: '%',
          label: 'PUE Improvement',
          description: 'Better power usage effectiveness scores',
        },
      ],
    },
    {
      title: 'Reliability & Uptime',
      description: 'Prevent outages and minimize impact through proactive monitoring.',
      metrics: [
        {
          value: 99.999,
          suffix: '%',
          label: 'Availability',
          description: 'Five-nines uptime maintained consistently',
        },
        {
          value: 15,
          suffix: 'min',
          label: 'Faster MTTD',
          description: 'Faster mean time to detect issues',
        },
      ],
    },
    {
      title: 'Operational Scale',
      description: 'Manage growing infrastructure without proportional headcount increases.',
      metrics: [
        {
          value: 50,
          suffix: 'K+',
          label: 'Sensors Managed',
          description: 'Per-facility sensor capacity',
        },
        {
          value: 3,
          suffix: 'x',
          label: 'Staff Efficiency',
          description: 'More facilities managed per ops team member',
        },
      ],
    },
  ],
  customerStories: [
    {
      name: 'Teraco',
      industry: 'Colocation Data Centers',
      headline: "Africa's largest data center provider unifying facility monitoring",
      metrics: [
        'Multiple data center campuses',
        'Thousands of environmental sensors',
        'Real-time cooling optimization',
        'Unified multi-site monitoring',
      ],
      quote:
        'HiveMQ gives us the real-time visibility we need to maintain five-nines uptime across all our facilities.',
    },
    {
      name: 'Liberty Global',
      industry: 'Telecommunications',
      headline: 'Modernizing data center infrastructure monitoring across Europe',
      metrics: [
        'Pan-European data center network',
        'Legacy system modernization',
        'Automated environmental controls',
        'Centralized operations dashboard',
      ],
    },
  ],
  challenges: [
    'Thousands of sensors generating massive data volumes per facility',
    'Critical uptime requirements with zero tolerance for monitoring gaps',
    'Legacy BMS systems that cannot integrate with modern analytics',
    'Cooling optimization across dynamically changing heat loads',
    'Multi-site visibility requiring unified data architecture',
    'Compliance requirements for data security and audit trails',
  ],
  hivemqSolution: [
    'High-throughput MQTT broker handling 50K+ sensor streams per facility',
    'Guaranteed message delivery ensuring no monitoring blind spots',
    'Protocol bridges connecting legacy BMS and DCIM to modern platforms',
    'Real-time analytics enabling dynamic cooling and power optimization',
    'Multi-site clustering providing unified cross-facility visibility',
    'Enterprise security with TLS encryption and comprehensive audit logging',
  ],
  ctaTitle: 'See Data Center Solutions in Action',
  ctaDescription:
    'Explore our interactive connected demo showcasing real-time environmental monitoring, power analytics, and multi-site management.',
  ctaRoute: '/connected',
}

export const logistics: IcpData = {
  id: 'logistics',
  title: 'Logistics & Transportation',
  shortDescription:
    'Track fleets in real time, optimize routing, and ensure cold chain integrity across global supply chains.',
  fullDescription:
    'HiveMQ powers logistics and transportation companies with real-time fleet tracking, dynamic route optimization, and end-to-end supply chain visibility. Our MQTT platform handles the unique challenges of mobile assets, intermittent connectivity, and global-scale operations.',
  icon: 'Truck',
  color: 'orange.500',
  gradient: { from: 'orange.600', to: 'orange.400' },
  keyMetrics: [
    {
      value: 20,
      suffix: '%',
      label: 'On-Time Improvement',
      description:
        'Increase in on-time delivery performance through real-time tracking and optimization',
    },
    {
      value: 15,
      suffix: '%',
      label: 'Fuel Reduction',
      description:
        'Decrease in fuel consumption through optimized routing and driver behavior monitoring',
    },
    {
      value: 1,
      suffix: 'M+',
      label: 'Location Updates/Day',
      description: 'Daily location update messages processed for fleet tracking operations',
    },
    {
      value: 35,
      suffix: '%',
      label: 'Fewer Cargo Losses',
      description: 'Reduction in cargo damage and loss through continuous condition monitoring',
    },
  ],
  valuePropositions: [
    {
      icon: 'MapPin',
      title: 'Fleet Tracking',
      description:
        'Track every vehicle, vessel, and asset in real time with high-frequency GPS and telemetry data.',
    },
    {
      icon: 'Route',
      title: 'Dynamic Routing',
      description:
        'Optimize routes in real time based on traffic, weather, and delivery constraints to reduce costs and delays.',
    },
    {
      icon: 'Thermometer',
      title: 'Cold Chain Monitoring',
      description:
        'Ensure temperature-sensitive cargo integrity with continuous monitoring and automated alerting.',
    },
    {
      icon: 'Bell',
      title: 'Proactive Alerts',
      description:
        'Receive instant notifications for delays, deviations, temperature excursions, and maintenance needs.',
    },
    {
      icon: 'Globe',
      title: 'Global Coverage',
      description:
        'Maintain connectivity across cellular, satellite, and Wi-Fi networks with seamless handoff and buffering.',
    },
    {
      icon: 'BarChart3',
      title: 'Analytics & Insights',
      description:
        'Transform fleet data into actionable insights for fuel optimization, driver safety, and route planning.',
    },
  ],
  businessOutcomes: [
    {
      title: 'Delivery Performance',
      description: 'Improve on-time delivery rates and customer satisfaction.',
      metrics: [
        {
          value: 20,
          suffix: '%',
          label: 'On-Time Improvement',
          description: 'Better on-time delivery rates',
        },
        {
          value: 25,
          suffix: '%',
          label: 'Customer Complaints Down',
          description: 'Fewer delivery-related complaints',
        },
      ],
    },
    {
      title: 'Cost Optimization',
      description: 'Reduce fleet operating costs through data-driven optimization.',
      metrics: [
        {
          value: 15,
          suffix: '%',
          label: 'Fuel Savings',
          description: 'Lower fuel costs through route optimization',
        },
        {
          value: 10,
          suffix: '%',
          label: 'Maintenance Savings',
          description: 'Reduced maintenance costs via predictive alerts',
        },
      ],
    },
    {
      title: 'Cargo Protection',
      description: 'Minimize losses through continuous condition monitoring and alerting.',
      metrics: [
        {
          value: 35,
          suffix: '%',
          label: 'Fewer Losses',
          description: 'Reduction in cargo damage and spoilage',
        },
        {
          value: 99,
          suffix: '%',
          label: 'Chain of Custody',
          description: 'End-to-end visibility of cargo conditions',
        },
      ],
    },
  ],
  customerStories: [
    {
      name: 'FELA Management',
      industry: 'Fleet Management',
      headline: 'Real-time fleet tracking and optimization for commercial vehicles',
      metrics: [
        'Large commercial fleet managed',
        'Real-time GPS tracking',
        'Driver behavior monitoring',
        'Fuel consumption optimization',
      ],
    },
    {
      name: 'SWM Munich',
      industry: 'Public Transportation',
      headline: "Connecting Munich's public transit for real-time passenger information",
      metrics: [
        'City-wide transit network',
        'Real-time vehicle positions',
        'Passenger information systems',
        'Schedule adherence monitoring',
      ],
      quote:
        'HiveMQ enables us to deliver real-time transit information to millions of passengers.',
    },
    {
      name: 'Air France-KLM',
      industry: 'Aviation',
      headline: 'Connected aircraft operations and ground handling optimization',
      metrics: [
        'Fleet-wide connectivity',
        'Ground operations optimization',
        'Baggage tracking integration',
        'Maintenance data streaming',
      ],
    },
    {
      name: 'Matternet',
      industry: 'Drone Delivery',
      headline: 'Autonomous drone delivery network with real-time flight telemetry',
      metrics: [
        'Autonomous drone operations',
        'Real-time flight telemetry',
        'Medical supply delivery',
        'Urban air mobility platform',
      ],
    },
  ],
  challenges: [
    'Tracking thousands of mobile assets across unreliable cellular networks',
    'Maintaining cold chain integrity for temperature-sensitive shipments',
    'Real-time routing decisions requiring instant data from moving vehicles',
    'Global operations spanning multiple regions and connectivity types',
    'Cargo theft and loss without continuous monitoring capabilities',
    'Driver safety monitoring requiring frequent telemetry updates',
  ],
  hivemqSolution: [
    'Reliable MQTT messaging with store-and-forward for intermittent connectivity',
    'Continuous temperature and condition monitoring with instant excursion alerts',
    'Sub-second position updates enabling real-time route optimization',
    'Global broker clustering with multi-region deployment support',
    'End-to-end cargo tracking with tamper detection and chain of custody',
    'High-frequency telemetry ingestion for driver behavior and vehicle health',
  ],
  ctaTitle: 'See Logistics Solutions in Action',
  ctaDescription:
    'Explore our interactive logistics demo showcasing fleet tracking, route optimization, and cold chain monitoring.',
  ctaRoute: '/logistics',
}

export const allIcps: IcpData[] = [smartManufacturing, energy, dataCenters, logistics]
